import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { builderSetShouldEditParties } from '~/actions/builder';
import { workflowSetPartiesSigners } from '~/actions/workflow';
import { Button } from '~/eds/src/components/Button/Button';
import { WorkflowIntakeFormType } from '~/enums/WorkflowIntakeFormType';
import { WorkflowPartyType } from '~/enums/WorkflowPartyType';
import { getPartiesWithSigners } from '~/reducers/workflow';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Text from '~/ui/components/Text/Text';
import { coerceName, getPartyName } from '~/utils/workflow';

import PartySignerCard from './PartySignerCard';

function getMaxSignerIndex(parties) {
  return Math.max(
    ...parties
      .map((party) => party.signers.map((signer) => signer.index))
      .flat(),
  );
}

function CreatePartySigner({
  // connected
  parties,
  builderSetShouldEditParties,
  workflowSetPartiesSigners,
}) {
  const intl = useIntl();
  const [partiesState, setPartiesState] = useState({
    internal: parties.internal,
    counter: parties.counter,
  });

  useEffect(() => {
    setPartiesState({
      internal: parties.internal,
      counter: parties.counter,
    });
  }, [parties]);

  if (!parties) {
    return null;
  }

  function handleAddParty({ type }) {
    const partyKey =
      type === WorkflowPartyType.Counterparty ? 'counter' : 'internal';

    setPartiesState((prevState) => {
      const prevParties = prevState[partyKey];
      const maxIndex = Math.max(...prevParties.map((party) => party.index));
      const newPartyIndex = Math.max(-1, maxIndex) + 1;
      const newParty = {
        id: uuid(),
        index: newPartyIndex,
        name: getPartyName(type, newPartyIndex),
        signers: [],
        type,
      };
      return {
        ...prevState,
        [partyKey]: [...prevParties, newParty],
      };
    });
  }

  function handleAddSigner({ partyId, type }) {
    const partyKey =
      type === WorkflowPartyType.Counterparty ? 'counter' : 'internal';

    setPartiesState((prevState) => {
      const signerMaxIndex = Math.max(
        getMaxSignerIndex(prevState.internal),
        getMaxSignerIndex(prevState.counter),
      );
      const newSignerIndex = Math.max(signerMaxIndex, -1) + 1;
      const newSigner = {
        id: uuid(),
        index: newSignerIndex,
        name: coerceName(
          null,
          intl.formatMessage({
            id: 'CreatePartySigner.defaultSignerName',
            defaultMessage: 'Signer',
            description: 'Default name for a new signer.',
          }),
          newSignerIndex,
        ),
        partyId,
      };
      const updatedParties = prevState[partyKey].map((party) => {
        if (party.id === partyId) {
          return {
            ...party,
            signers: [...party.signers, newSigner],
          };
        } else {
          return party;
        }
      });
      return {
        ...prevState,
        [partyKey]: updatedParties,
      };
    });
  }

  function handleRemoveParty({ partyId, type }) {
    const partyKey =
      type === WorkflowPartyType.Counterparty ? 'counter' : 'internal';

    setPartiesState((prevState) => ({
      ...prevState,
      [partyKey]: prevState[partyKey].filter((party) => party.id !== partyId),
    }));
  }

  function handleRemoveSigner({ signerId, type }) {
    const partyKey =
      type === WorkflowPartyType.Counterparty ? 'counter' : 'internal';

    setPartiesState((prevState) => ({
      ...prevState,
      [partyKey]: prevState[partyKey].map((party) => ({
        ...party,
        signers: party.signers.filter((signer) => signer.id !== signerId),
      })),
    }));
  }

  return (
    <FlexLayout alignItems="center" bg="white" flexDirection="column" pb={12}>
      <FlexLayout justifyContent="center">
        <Text variant="xl-dense-bold" sx={{ justifySelf: 'center' }}>
          {intl.formatMessage({
            id: 'CreatePartySigner.editPartiesSignersTitle',
            defaultMessage: 'Edit Parties and Signers',
            description: 'Page title for editing parties and signers',
          })}
        </Text>
      </FlexLayout>
      <FlexLayout mt={8} space={2}>
        <PartySignerCard
          parties={partiesState.internal}
          title={intl.formatMessage({
            id: 'CreatePartySigner.internalPartiesTitle',
            defaultMessage: 'Internal parties',
            description: 'Title for internal parties section',
          })}
          type={WorkflowIntakeFormType.Company}
          onAddParty={handleAddParty}
          onAddSigner={handleAddSigner}
          onRemoveParty={handleRemoveParty}
          onRemoveSigner={handleRemoveSigner}
        />
        <PartySignerCard
          parties={partiesState.counter}
          title={intl.formatMessage({
            id: 'CreatePartySigner.counterpartiesTitle',
            defaultMessage: 'Counterparties',
            description: 'Title for counterparties section',
          })}
          type={WorkflowIntakeFormType.Counterparty}
          onAddParty={handleAddParty}
          onAddSigner={handleAddSigner}
          onRemoveParty={handleRemoveParty}
          onRemoveSigner={handleRemoveSigner}
        />
      </FlexLayout>
      <FlexLayout
        alignItems="center"
        justifyContent="center"
        sx={{
          bg: 'white',
          bottom: 0,
          boxShadow: 'page-footer-top',
          height: 'modal-footer-height',
          left: 0,
          position: 'fixed',
          right: 0,
        }}
      >
        <FlexLayout justifyContent="flex-end" space={4} sx={{ width: '808px' }}>
          <Button
            text={intl.formatMessage({
              id: 'CreatePartySigner.cancelButton',
              defaultMessage: 'Cancel',
              description: 'Cancel button text',
            })}
            variant="ghost"
            onClick={() => builderSetShouldEditParties(false)}
          />
          <Button
            text={intl.formatMessage({
              id: 'CreatePartySigner.saveButton',
              defaultMessage: 'Save',
              description: 'Save button text',
            })}
            variant="primary"
            onClick={() => {
              workflowSetPartiesSigners([
                ...partiesState.internal,
                ...partiesState.counter,
              ]);
              builderSetShouldEditParties(false);
            }}
          />
        </FlexLayout>
      </FlexLayout>
    </FlexLayout>
  );
}

function mapStateToProps({ workflow }) {
  return {
    parties: getPartiesWithSigners(workflow),
  };
}

export default connect(mapStateToProps, {
  builderSetShouldEditParties,
  workflowSetPartiesSigners,
})(CreatePartySigner);
